/**
 * ImageCacheService - Simplified image caching service
 * 
 * Features:
 * - In-memory URL tracking to avoid duplicate fetches
 * - Uses Service Worker for actual HTTP caching
 * - Simple preloading mechanism
 * - Cache statistics
 * - Private mode detection and graceful degradation
 */

import { isPrivateModeSync } from '../utils/privateMode'

class ImageCacheService {
    constructor(options = {}) {
        this.cachedUrls = new Set() // Track which URLs we've fetched
        this.pendingFetches = new Map() // Track in-progress fetches
        this.isPrivateMode = isPrivateModeSync()
        this.stats = {
            fetches: 0,
            hits: 0,
            errors: 0,
            privateMode: this.isPrivateMode
        }

        if (this.isPrivateMode) {
        }
    }

    /**
     * Get cached image - just ensures it's fetched once
     * In private mode, always returns original URL without caching
     * @param {string} url - Original image URL
     * @returns {Promise<string>} - Original URL
     */
    async getCachedImage(url) {
        if (!url) return null

        // In private mode, just return the URL without any caching
        if (this.isPrivateMode) {
            return url
        }

        // If already cached, just return the URL
        if (this.cachedUrls.has(url)) {
            this.stats.hits++
            return url
        }

        // If currently being fetched, wait for that fetch
        if (this.pendingFetches.has(url)) {
            return this.pendingFetches.get(url)
        }

        // Start new fetch
        const fetchPromise = this.fetchImage(url)
        this.pendingFetches.set(url, fetchPromise)

        try {
            await fetchPromise
            this.cachedUrls.add(url)
            this.stats.fetches++
            return url
        } catch (error) {
            this.stats.errors++
            console.error('Failed to fetch image:', error)
            // Still return URL - Service Worker might have it cached
            return url
        } finally {
            this.pendingFetches.delete(url)
        }
    }

    /**
     * Fetch image to trigger caching
     */
    async fetchImage(url) {
        // Skip fetching in private mode
        if (this.isPrivateMode) {
            return
        }

        try {
            // Check if URL is external (S3)
            const isExternal = url.includes('s3.amazonaws.com') || !url.startsWith(window.location.origin);

            const fetchOptions = {
                // Only include credentials for same-origin requests
                credentials: isExternal ? 'omit' : 'include',
                // Use 'cors' mode but handle failures
                mode: 'cors'
            };

            // If it's an S3 URL, we might need to be careful with CORS
            // Standard img tags don't send credentials, so we shouldn't either for S3

            const response = await fetch(url, fetchOptions)

            if (!response.ok) {
                throw new Error(`Failed to fetch: ${response.status}`)
            }

            // Just fetch it - Service Worker will cache
            await response.blob()
        } catch (error) {
            // Don't throw - let the image tag handle errors
            console.warn('Image fetch failed:', url, error)

            // If CORS failed, we might try no-cors for opaque caching if needed,
            // but for now, just letting it fail gracefully is better than crashing
        }
    }

    /**
     * Preload multiple images
     * Disabled in private mode
     * @param {string[]} urls - Array of image URLs to preload
     * @returns {Promise<void>}
     */
    async preloadImages(urls) {
        if (!urls || !Array.isArray(urls)) return

        // Skip preloading in private mode
        if (this.isPrivateMode) {
            return
        }

        // Deduplicate URLs
        const uniqueUrls = [...new Set(urls)].filter(url =>
            url && !this.cachedUrls.has(url)
        )

        // Preload in batches of 5 to avoid overwhelming the network
        const batchSize = 5
        for (let i = 0; i < uniqueUrls.length; i += batchSize) {
            const batch = uniqueUrls.slice(i, i + batchSize)
            await Promise.allSettled(
                batch.map(url => this.getCachedImage(url))
            )
        }
    }

    /**
     * Clear cache tracking
     */
    clearCache() {
        this.cachedUrls.clear()
        this.pendingFetches.clear()
        this.stats = {
            fetches: 0,
            hits: 0,
            errors: 0,
            privateMode: this.isPrivateMode
        }
    }

    /**
     * Get cache statistics
     */
    getCacheStats() {
        return {
            ...this.stats,
            cachedCount: this.cachedUrls.size,
            pendingCount: this.pendingFetches.size,
            hitRate: this.stats.fetches > 0
                ? this.stats.hits / this.stats.fetches
                : 0
        }
    }
}

// Create singleton instance
const imageCacheService = new ImageCacheService()

// Expose for debugging
if (typeof window !== 'undefined') {
    window.imageCacheService = imageCacheService
}

export default imageCacheService
